/*
Purpose: Casefile-scoped Tracker tab - Monitor all scans/analysis for this casefile
Audience: Casefile viewers monitoring automated or manual scans
What it does:
- All Scans: View all orders/jobs with toggle between grouped (Orders) or flat (Jobs) view
- Live Feed: Real-time monitoring of recent scans (last hour/day) with auto-refresh
- Timeline: Historical view with activity patterns and statistics over time
*/
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import OrdersListView from '../modals/results/OrdersListView';
import SingleOrderResults from '../modals/results/SingleOrderResults';
import { useOrdersWithJobsData } from '../../hooks/useOrdersWithJobsData';
import JobCard from '../results/display/cards/JobCard';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { useResultsData } from '../../hooks/useResultsData';

const CasefileTrackerTab = ({ casefileId, currentUser }) => {
  const [viewState, setViewState] = useState({ mode: 'list', orderId: null, jobId: null });
  const [subTab, setSubTab] = useState('all_scans'); // 'all_scans' | 'live_feed' | 'timeline'
  const [allScansView, setAllScansView] = useState('orders'); // 'orders' | 'jobs'
  const [liveFeedPeriod, setLiveFeedPeriod] = useState('hour'); // 'hour' | 'day'
  const [allJobs, setAllJobs] = useState([]);
  const [allJobsLoading, setAllJobsLoading] = useState(true);
  
  // Filters for All Scans tab
  const [userFilter, setUserFilter] = useState('all'); // 'all' | 'owner'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' | 'desc'
  const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState(false);
  
  // Advanced filters
  const [providerFilter, setProviderFilter] = useState('all');
  const [targetsFilter, setTargetsFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Filters: constrain to this casefile only
  const filters = useMemo(() => ({ casefileIds: [casefileId] }), [casefileId]);

  const modalData = useOrdersWithJobsData({
    filters,
    pageSize: 20,
    modalId: `casefile_tracker_${casefileId}`,
    enableRealTimeJobs: true,
    enableResultsParsing: true
  });

  const openOrder = useCallback((orderId) => setViewState({ mode: 'order', orderId, jobId: null }), []);
  const openJob = useCallback((orderId, jobId) => setViewState({ mode: 'job', orderId, jobId }), []);
  const backToList = useCallback(() => setViewState({ mode: 'list', orderId: null, jobId: null }), []);

  // Subscribe to ALL jobs for this casefile (for Live Feed and Timeline views)
  useEffect(() => {
    if (!casefileId) return;
    setAllJobsLoading(true);
    const q = query(
      collection(db, 'jobs'), 
      where('casefileId', '==', casefileId),
      orderBy('createdAt', 'desc')
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      const jobs = [];
      snap.forEach(doc => jobs.push({ id: doc.id, ...doc.data() }));
      setAllJobs(jobs);
      setAllJobsLoading(false);
    }, (err) => {
      console.error('Error subscribing to jobs', err);
      setAllJobsLoading(false);
    });
    return () => unsubscribe();
  }, [casefileId]);

  // Filter and sort jobs for All Scans tab
  const filteredAndSortedJobs = useMemo(() => {
    let filtered = [...allJobs];
    
    // Apply user filter
    if (userFilter === 'owner' && currentUser?.uid) {
      filtered = filtered.filter(job => job.userId === currentUser.uid);
    }
    
    // Apply provider filter
    if (providerFilter !== 'all') {
      filtered = filtered.filter(job => job.provider === providerFilter);
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'completed') {
        filtered = filtered.filter(job => job.status === 'completed' || job.status === 'success');
      } else if (statusFilter === 'in_progress') {
        filtered = filtered.filter(job => job.status === 'processing' || job.status === 'pending' || job.status === 'queued');
      } else if (statusFilter === 'failed') {
        filtered = filtered.filter(job => job.status === 'failed' || job.status === 'error');
      }
    }
    
    // Apply sort order
    filtered.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(0);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
    
    return filtered;
  }, [allJobs, userFilter, sortOrder, providerFilter, statusFilter, currentUser?.uid]);
  
  // Count active advanced filters
  const activeAdvancedFilters = useMemo(() => {
    let count = 0;
    if (providerFilter !== 'all') count++;
    if (targetsFilter !== 'all') count++;
    if (statusFilter !== 'all') count++;
    return count;
  }, [providerFilter, targetsFilter, statusFilter]);
  
  // Get unique providers from jobs
  const availableProviders = useMemo(() => {
    const providers = new Set(allJobs.map(job => job.provider).filter(Boolean));
    return Array.from(providers);
  }, [allJobs]);

  // Filter jobs for Live Feed based on time period
  const liveFeedJobs = useMemo(() => {
    const now = new Date();
    const cutoff = new Date(now.getTime() - (liveFeedPeriod === 'hour' ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000));
    return allJobs.filter(job => {
      const jobDate = job.createdAt?.toDate?.() || new Date(0);
      return jobDate >= cutoff;
    });
  }, [allJobs, liveFeedPeriod]);

  // Pool results for live feed jobs
  const liveFeedJobIds = useMemo(() => liveFeedJobs.map(j => j.id), [liveFeedJobs]);
  const liveFeedResults = useResultsData({ jobIds: liveFeedJobIds, autoLoad: true, parseContent: true });

  // Auto-refresh Live Feed every 10 seconds
  useEffect(() => {
    if (subTab !== 'live_feed') return;
    const interval = setInterval(() => {
      // The real-time listener will automatically update, but we force a re-render
      setAllJobsLoading(prev => prev);
    }, 10000);
    return () => clearInterval(interval);
  }, [subTab]);

  // Calculate statistics for all tabs
  const statistics = useMemo(() => {
    const total = allJobs.length;
    const completed = allJobs.filter(j => j.status === 'completed' || j.status === 'success').length;
    const inProgress = allJobs.filter(j => j.status === 'processing' || j.status === 'pending' || j.status === 'queued').length;
    const failed = allJobs.filter(j => j.status === 'failed' || j.status === 'error').length;
    
    return { total, completed, inProgress, failed };
  }, [allJobs]);

  if (viewState.mode === 'order' || viewState.mode === 'job') {
    return (
      <div className="tracker-single-order">
        <div className="tracker-header">
          <button className="btn-secondary" onClick={backToList}>Back to Tracker</button>
        </div>
        <SingleOrderResults
          orderId={viewState.orderId}
          focusJobId={viewState.jobId}
          currentUser={currentUser}
          onNavigateToOrder={(orderId) => setViewState({ mode: 'order', orderId, jobId: null })}
        />
      </div>
    );
  }

  return (
    <div className="casefile-tracker-tab">
      <div className="tab-header">
        <p className="tab-subtitle">Monitor all scans and analysis for this casefile</p>
      </div>

      {/* Sub-tabs */}
      <div className="tabs-header sub-tabs">
        <button 
          className={`tab-btn ${subTab === 'all_scans' ? 'active' : ''}`}
          onClick={() => setSubTab('all_scans')}
          title="All Scans: View all orders and jobs for this casefile"
        >All Scans <span className="help-tip">?</span></button>
        <button 
          className={`tab-btn ${subTab === 'live_feed' ? 'active' : ''}`}
          onClick={() => setSubTab('live_feed')}
          title="Live Feed: Real-time monitoring of recent scans"
        >Live Feed <span className="help-tip">?</span></button>
        <button 
          className={`tab-btn ${subTab === 'timeline' ? 'active' : ''}`}
          onClick={() => setSubTab('timeline')}
          title="Timeline: Historical view with activity patterns"
        >Timeline <span className="help-tip">?</span></button>
      </div>

      {/* All Scans Tab */}
      {subTab === 'all_scans' && (
        <div className="all-scans-section">
          {/* View Toggle and Filters */}
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            marginBottom: '16px', 
            marginTop: '12px',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}>
            {/* View Toggle */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className={`btn-toggle ${allScansView === 'orders' ? 'active' : ''}`}
                onClick={() => setAllScansView('orders')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: allScansView === 'orders' ? 'rgba(255,107,53,0.2)' : 'transparent',
                  color: allScansView === 'orders' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Orders
              </button>
              <button
                className={`btn-toggle ${allScansView === 'jobs' ? 'active' : ''}`}
                onClick={() => setAllScansView('jobs')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: allScansView === 'jobs' ? 'rgba(255,107,53,0.2)' : 'transparent',
                  color: allScansView === 'jobs' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Jobs
              </button>
            </div>

            {/* Divider */}
            <div style={{ 
              width: '1px', 
              height: '24px', 
              background: 'rgba(255,255,255,0.2)',
              display: 'none',
              '@media (minWidth: 640px)': { display: 'block' }
            }} />

            {/* Filters - Compact Design */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* User Filter */}
              <select
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                style={{
                  padding: '6px 12px',
                  paddingRight: '32px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)',
                  color: 'rgba(255,255,255,0.9)',
                  cursor: 'pointer',
                  fontSize: '13px',
                  outline: 'none',
                  appearance: 'none',
                  backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'rgba(255,255,255,0.5)\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 8px center',
                  backgroundSize: '16px'
                }}
              >
                <option value="all">👥 All Users</option>
                <option value="owner">👤 My Scans</option>
              </select>

              {/* Sort Order */}
              <button
                onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.05)',
                  color: 'rgba(255,255,255,0.9)',
                  cursor: 'pointer',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s'
                }}
                title={sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
              >
                {sortOrder === 'desc' ? '↓ Newest' : '↑ Oldest'}
              </button>

              {/* Advanced Filters Button */}
              <button
                onClick={() => setIsAdvancedFilterOpen(true)}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: activeAdvancedFilters > 0 ? 'rgba(255,107,53,0.2)' : 'rgba(255,255,255,0.05)',
                  color: activeAdvancedFilters > 0 ? '#ff6b35' : 'rgba(255,255,255,0.9)',
                  cursor: 'pointer',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s',
                  position: 'relative'
                }}
                title="Advanced Filters"
              >
                ⚙️
                {activeAdvancedFilters > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    background: '#ff6b35',
                    color: 'white',
                    borderRadius: '50%',
                    width: '16px',
                    height: '16px',
                    fontSize: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold'
                  }}>
                    {activeAdvancedFilters}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Advanced Filters Modal */}
          {isAdvancedFilterOpen && (
            <AdvancedFiltersModal
              isOpen={isAdvancedFilterOpen}
              onClose={() => setIsAdvancedFilterOpen(false)}
              providerFilter={providerFilter}
              setProviderFilter={setProviderFilter}
              targetsFilter={targetsFilter}
              setTargetsFilter={setTargetsFilter}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              availableProviders={availableProviders}
              onReset={() => {
                setProviderFilter('all');
                setTargetsFilter('all');
                setStatusFilter('all');
              }}
            />
          )}

          {allScansView === 'orders' ? (
        <OrdersListView
          orders={modalData.orders}
          expandedOrders={modalData.expandedOrders}
          jobsByOrderId={modalData.rawData.jobs.jobsByOrderId}
          resultsByJobId={modalData.results}
          onToggleExpand={modalData.toggleOrderExpansion}
          onEnsureJobsLoaded={async () => { /* real-time; no-op */ }}
          onOpenOrder={openOrder}
          onOpenJob={openJob}
          hasMore={modalData.hasMore}
          isLoading={modalData.loading}
          onLoadMore={modalData.loadMore}
              showFilterBar={false}
            />
          ) : (
            <div className="jobs-flat-view" style={{ display: 'grid', gap: 12 }}>
              {allJobsLoading ? (
                <div style={{ opacity: 0.8, padding: '20px', textAlign: 'center' }}>Loading jobs…</div>
              ) : filteredAndSortedJobs.length === 0 ? (
                <div style={{ opacity: 0.8, padding: '20px', textAlign: 'center' }}>
                  {userFilter === 'owner' ? 'No scans found from you in this casefile.' : 'No scans found for this casefile.'}
                </div>
              ) : (
                filteredAndSortedJobs.map(job => (
                  <JobCard
                    key={job.id}
                    job={job}
                    resultsMeta={modalData.results[job.id]}
                    size="regular"
                    onOpen={() => openJob(job.orderId, job.id)}
                  />
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Live Feed Tab */}
      {subTab === 'live_feed' && (
        <div className="live-feed-section">
          {/* Period Toggle */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', marginTop: '12px', alignItems: 'center' }}>
            <span style={{ opacity: 0.7, fontSize: '14px' }}>Show:</span>
            <button
              className={`btn-toggle ${liveFeedPeriod === 'hour' ? 'active' : ''}`}
              onClick={() => setLiveFeedPeriod('hour')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid rgba(255,255,255,0.2)',
                background: liveFeedPeriod === 'hour' ? 'rgba(255,107,53,0.2)' : 'transparent',
                color: liveFeedPeriod === 'hour' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Last Hour
            </button>
            <button
              className={`btn-toggle ${liveFeedPeriod === 'day' ? 'active' : ''}`}
              onClick={() => setLiveFeedPeriod('day')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid rgba(255,255,255,0.2)',
                background: liveFeedPeriod === 'day' ? 'rgba(255,107,53,0.2)' : 'transparent',
                color: liveFeedPeriod === 'day' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Last 24 Hours
            </button>
            <span style={{ opacity: 0.5, fontSize: '12px', marginLeft: 'auto' }}>
              🔄 Auto-refreshing every 10 seconds
            </span>
          </div>

          <div className="live-feed-jobs" style={{ display: 'grid', gap: 12 }}>
            {allJobsLoading ? (
              <div style={{ opacity: 0.8, padding: '20px', textAlign: 'center' }}>Loading live feed…</div>
            ) : liveFeedJobs.length === 0 ? (
              <div style={{ opacity: 0.8, padding: '20px', textAlign: 'center' }}>
                No scans in the {liveFeedPeriod === 'hour' ? 'last hour' : 'last 24 hours'}.
              </div>
            ) : (
              liveFeedJobs.map(job => (
                <JobCard
                  key={job.id}
                  job={job}
                  resultsMeta={liveFeedResults.pooledResults[job.id]}
                  size="regular"
                  onOpen={() => openJob(job.orderId, job.id)}
                />
              ))
              )}
            </div>
        </div>
      )}

      {/* Timeline Tab */}
      {subTab === 'timeline' && (
        <TimelineView 
          jobs={allJobs} 
          loading={allJobsLoading}
          onOpenJob={openJob}
          statistics={statistics}
        />
      )}
    </div>
  );
};

// Statistics Card Component
const StatCard = ({ label, value, color }) => (
  <div style={{
    padding: '16px',
    borderRadius: '8px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  }}>
    <div style={{ fontSize: '14px', opacity: 0.7 }}>{label}</div>
    <div style={{ fontSize: '32px', fontWeight: 'bold', color }}>{value}</div>
  </div>
);

// Timeline View Component
const TimelineView = ({ jobs, loading, onOpenJob, statistics }) => {
  const [isStatsExpanded, setIsStatsExpanded] = useState(false);

  // Group jobs by date
  const jobsByDate = useMemo(() => {
    const groups = {};
    jobs.forEach(job => {
      const date = job.createdAt?.toDate?.() || new Date();
      const dateKey = date.toLocaleDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(job);
    });
    return groups;
  }, [jobs]);

  // Sort dates descending
  const sortedDates = useMemo(() => {
    return Object.keys(jobsByDate).sort((a, b) => new Date(b) - new Date(a));
  }, [jobsByDate]);

  // Calculate hourly distribution for chart
  const hourlyDistribution = useMemo(() => {
    const hours = new Array(24).fill(0);
    jobs.forEach(job => {
      const date = job.createdAt?.toDate?.() || new Date();
      const hour = date.getHours();
      hours[hour]++;
    });
    return hours;
  }, [jobs]);

  const maxHourly = Math.max(...hourlyDistribution, 1);

  if (loading) {
    return <div style={{ opacity: 0.8, padding: '20px', textAlign: 'center' }}>Loading timeline…</div>;
  }

  if (jobs.length === 0) {
    return <div style={{ opacity: 0.8, padding: '20px', textAlign: 'center' }}>No scans found for timeline.</div>;
  }

  return (
    <div className="timeline-view" style={{ marginTop: '12px' }}>
      {/* Activity Pattern Chart */}
      <div style={{ 
        marginBottom: '24px',
        padding: '20px',
        background: 'rgba(255,255,255,0.05)',
        borderRadius: '8px',
        border: '1px solid rgba(255,255,255,0.1)'
      }}>
        <h3 style={{ marginBottom: '16px', fontSize: '16px', opacity: 0.9 }}>Activity Pattern (24 Hours)</h3>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '120px' }}>
          {hourlyDistribution.map((count, hour) => (
            <div key={hour} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
              <div 
                style={{
                  width: '100%',
                  height: `${(count / maxHourly) * 100}%`,
                  background: count > 0 ? 'linear-gradient(to top, #ff6b35, #f59e0b)' : 'rgba(255,255,255,0.1)',
                  borderRadius: '4px 4px 0 0',
                  position: 'relative',
                  minHeight: count > 0 ? '8px' : '4px'
                }}
                title={`${hour}:00 - ${count} scans`}
              >
                {count > 0 && (
                  <div style={{
                    position: 'absolute',
                    top: '-20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontSize: '10px',
                    opacity: 0.7
                  }}>{count}</div>
                )}
              </div>
              <div style={{ fontSize: '9px', opacity: 0.5 }}>{hour}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Statistics Section - Collapsible */}
      <div style={{ 
        marginBottom: '24px',
        padding: '16px',
        background: 'rgba(255,255,255,0.05)',
        borderRadius: '8px',
        border: '1px solid rgba(255,255,255,0.1)'
      }}>
        <div 
          onClick={() => setIsStatsExpanded(!isStatsExpanded)}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            cursor: 'pointer',
            marginBottom: isStatsExpanded ? '16px' : '0'
          }}
        >
          <h3 style={{ fontSize: '16px', opacity: 0.9 }}>Statistics</h3>
          <div style={{ 
            transform: isStatsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s ease',
            fontSize: '20px',
            opacity: 0.7
          }}>
            ▼
          </div>
        </div>
        
        {isStatsExpanded && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '12px',
            marginTop: '4px'
          }}>
            <StatCard label="Total Scans" value={statistics.total} color="#3b82f6" />
            <StatCard label="Completed" value={statistics.completed} color="#22c55e" />
            <StatCard label="In Progress" value={statistics.inProgress} color="#f59e0b" />
            <StatCard label="Failed" value={statistics.failed} color="#ef4444" />
          </div>
        )}
      </div>

      {/* Chronological Timeline */}
      <div className="timeline-chronological">
        <h3 style={{ marginBottom: '16px', fontSize: '16px', opacity: 0.9 }}>Scan History</h3>
        {sortedDates.map(dateKey => (
          <div key={dateKey} style={{ marginBottom: '24px' }}>
            <div style={{ 
              fontSize: '14px', 
              fontWeight: 'bold', 
              opacity: 0.7, 
              marginBottom: '12px',
              paddingBottom: '8px',
              borderBottom: '1px solid rgba(255,255,255,0.1)'
            }}>
              {dateKey} ({jobsByDate[dateKey].length} scans)
            </div>
            <div style={{ display: 'grid', gap: '8px' }}>
              {jobsByDate[dateKey].map(job => (
                <div 
                  key={job.id}
                  onClick={() => onOpenJob(job.orderId, job.id)}
                  style={{
                    padding: '12px',
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '6px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,107,53,0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                >
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <StatusBadge status={job.status} />
                    <div>
                      <div style={{ fontSize: '14px' }}>{job.provider || 'Unknown Provider'}</div>
                      <div style={{ fontSize: '12px', opacity: 0.6 }}>
                        {job.createdAt?.toDate?.().toLocaleTimeString() || 'Unknown time'}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', opacity: 0.5 }}>
                    {job.id.substring(0, 8)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const colors = {
    completed: '#22c55e',
    success: '#22c55e',
    processing: '#f59e0b',
    pending: '#f59e0b',
    queued: '#3b82f6',
    failed: '#ef4444',
    error: '#ef4444'
  };
  
  const color = colors[status] || '#6b7280';
  
  return (
    <div style={{
      padding: '4px 8px',
      borderRadius: '4px',
      background: `${color}20`,
      color: color,
      fontSize: '11px',
      fontWeight: 'bold',
      textTransform: 'uppercase'
    }}>
      {status || 'unknown'}
    </div>
  );
};

// Advanced Filters Modal Component
const AdvancedFiltersModal = ({
  isOpen,
  onClose,
  providerFilter,
  setProviderFilter,
  targetsFilter,
  setTargetsFilter,
  statusFilter,
  setStatusFilter,
  availableProviders,
  onReset
}) => {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          zIndex: 1000,
          backdropFilter: 'blur(4px)'
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: '#1a1f2e',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.2)',
          padding: '24px',
          zIndex: 1001,
          maxWidth: '500px',
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>⚙️ Advanced Filters</h3>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.7)',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
            }}
          >
            ×
          </button>
        </div>

        {/* Filter Options */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Provider Filter */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '8px',
              opacity: 0.9
            }}>
              Provider
            </label>
            <select
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)',
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                fontSize: '14px',
                outline: 'none'
              }}
            >
              <option value="all">All Providers</option>
              {availableProviders.map(provider => (
                <option key={provider} value={provider}>{provider}</option>
              ))}
            </select>
          </div>

          {/* Targets Filter */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '8px',
              opacity: 0.9
            }}>
              Targets
            </label>
            <select
              value={targetsFilter}
              onChange={(e) => setTargetsFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)',
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                fontSize: '14px',
                outline: 'none'
              }}
            >
              <option value="all">All Targets</option>
              <option value="vehicles">Vehicles</option>
              <option value="people">People</option>
              <option value="plates">License Plates</option>
              <option value="objects">Objects</option>
              <option value="text">Text/OCR</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '8px',
              opacity: 0.9
            }}>
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.05)',
                color: 'rgba(255,255,255,0.9)',
                cursor: 'pointer',
                fontSize: '14px',
                outline: 'none'
              }}
            >
              <option value="all">All Statuses</option>
              <option value="completed">✅ Completed</option>
              <option value="in_progress">⏳ In Progress</option>
              <option value="failed">❌ Failed</option>
            </select>
          </div>
        </div>

        {/* Footer Actions */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginTop: '24px',
          paddingTop: '16px',
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}>
          <button
            onClick={() => {
              onReset();
              onClose();
            }}
            style={{
              flex: 1,
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'transparent',
              color: 'rgba(255,255,255,0.9)',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            Reset All
          </button>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '10px 16px',
              borderRadius: '8px',
              border: 'none',
              background: '#ff6b35',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#ff5722'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#ff6b35'}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
};

export default CasefileTrackerTab;


